import { memo, useCallback } from 'react';
import {
  makeStyles,
  tokens,
  Text,
  mergeClasses,
} from '@fluentui/react-components';
import type { TemplateManifest } from '@/types/template';
import { CATEGORY_DISPLAY } from '@/types/template';
import { ConnectorIconRow } from './ConnectorIconRow';

// =============================================================================
// STYLES
// =============================================================================

const useStyles = makeStyles({
  card: {
    display: 'flex',
    flexDirection: 'column',
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalL}`,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    cursor: 'pointer',
    transitionProperty: 'background-color, box-shadow, border-color',
    transitionDuration: '0.15s',
    transitionTimingFunction: 'ease',
    overflow: 'hidden',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      boxShadow: tokens.shadow4,
      border: `1px solid ${tokens.colorNeutralStroke1}`,
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '2px',
    },
    ':active': {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
    },
  },
  cardCompact: {
    height: '100px',
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
  },
  sourceRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexShrink: 0,
    marginBottom: tokens.spacingVerticalXS,
  },
  communityLabel: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground3,
  },
  name: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
  },
  nameCompact: {
    WebkitLineClamp: 2,
    whiteSpace: 'nowrap',
    display: 'block',
    textOverflow: 'ellipsis',
  },
  description: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    flex: 1,
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 1,
    WebkitBoxOrient: 'vertical',
    lineHeight: tokens.lineHeightBase300,
  },
  bottomSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    marginTop: 'auto',
    paddingTop: tokens.spacingVerticalL,
  },
  tagRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
  },
  tag: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: `2px ${tokens.spacingHorizontalSNudge}`,
    borderRadius: tokens.borderRadiusMedium,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    whiteSpace: 'nowrap',
    lineHeight: tokens.lineHeightBase200,
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground2,
  },
});

// =============================================================================
// PROPS
// =============================================================================

export interface TemplateCardProps {
  template: TemplateManifest;
  onClick: (templateId: string) => void;
  /** 'full' (default) for gallery grid, 'compact' for empty canvas / create dialog */
  variant?: 'full' | 'compact';
}

// =============================================================================
// COMPONENT
// =============================================================================

export const TemplateCard: React.FC<TemplateCardProps> = memo(({ template, onClick, variant = 'full' }) => {
  const styles = useStyles();
  const isCompact = variant === 'compact';

  const handleClick = useCallback(() => {
    onClick(template.id);
  }, [onClick, template.id]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick(template.id);
      }
    },
    [onClick, template.id]
  );

  return (
    <div
      className={mergeClasses(
        styles.card,
        isCompact && styles.cardCompact,
      )}
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-label={`View template details: ${template.name}`}
    >
      {/* Community source indicator — full variant only */}
      {!isCompact && template.source === 'community' && (
        <div className={styles.sourceRow}>
          <Text className={styles.communityLabel}>Community</Text>
        </div>
      )}

      {/* Title */}
      <Text className={mergeClasses(styles.name, isCompact && styles.nameCompact)} title={template.name}>
        {template.name}
      </Text>

      {/* Description — compact variant only */}
      {isCompact && (
        <Text className={styles.description}>
          {template.description}
        </Text>
      )}

      {/* Bottom section — full variant only */}
      {!isCompact && (
        <div className={styles.bottomSection}>
          {/* Category tag(s) */}
          <div className={styles.tagRow}>
            {template.category.map((c) => (
              <span key={c} className={styles.tag}>
                {CATEGORY_DISPLAY[c] ?? c}
              </span>
            ))}
          </div>

          {/* Connector icons */}
          {template.featuredConnectors && template.featuredConnectors.length > 0 && (
            <ConnectorIconRow connectors={template.featuredConnectors} />
          )}
        </div>
      )}
    </div>
  );
});

TemplateCard.displayName = 'TemplateCard';
